import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const OP_DA_VINCI_SYSTEM_ID_CONDITION_ID =
  'intuitiveOpDaVinciSystemId';
export const FilterConditionOpDaVinciSystemIdPluginType: FilterConditionPluginSpecification<
  Omit<JsonOpDaVinciSystemIdPluginDto, 'type'>,
  JsonOpDaVinciSystemIdPluginDto,
  OpDaVinciSystemIdCondition
> = {
  type: 'intuitiveOpDaVinciSystemId',
  category: 'case',
  displayName: {
    de: 'da Vinci System',
  },
  sortingIndex: 8,
  occurrence: 'single',
  newInstance(): FilterConditionOpDaVinciSystemIdPlugin {
    return new FilterConditionOpDaVinciSystemIdPlugin([], null);
  },
  fromCondition(
    jsonObj: OpDaVinciSystemIdCondition,
  ): FilterConditionPluginImpl<
      Omit<JsonOpDaVinciSystemIdPluginDto, 'type'>
    > {
    return new FilterConditionOpDaVinciSystemIdPlugin(
        jsonObj.includes ? jsonObj.includes : null,
        jsonObj.excludes ? jsonObj.excludes : null,
      );
  },
  fromObject(
    obj: JsonOpDaVinciSystemIdPluginDto,
  ): FilterConditionPluginImpl<
      Omit<JsonOpDaVinciSystemIdPluginDto, 'type'>
    > {
    return new FilterConditionOpDaVinciSystemIdPlugin(
        obj.includes ? obj.includes : null,
        obj.excludes ? obj.excludes : null,
      );
  },
};

export class FilterConditionOpDaVinciSystemIdPlugin extends FilterConditionPluginImpl<
  Omit<JsonOpDaVinciSystemIdPluginDto, 'type'>,
  JsonOpDaVinciSystemIdPluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    Omit<JsonOpDaVinciSystemIdPluginDto, 'type'>,
    JsonOpDaVinciSystemIdPluginDto,
    OpDaVinciSystemIdCondition
  > = FilterConditionOpDaVinciSystemIdPluginType;

  constructor(
    protected includes: string[],
    protected excludes: string[],
  ) {
    super(FilterConditionOpDaVinciSystemIdPluginType.type);
  }

  override equals(
    other: FilterConditionOpDaVinciSystemIdPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  clone(): FilterConditionOpDaVinciSystemIdPlugin {
    return new FilterConditionOpDaVinciSystemIdPlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  toCondition(): BdCondition | null {
    return new OpDaVinciSystemIdCondition(this.includes, this.excludes);
  }

  toJSON(): JsonOpDaVinciSystemIdPluginDto | null {
    return {
      type: this.type,
      includes: this.includes ? this.includes : null,
      excludes: this.excludes ? this.excludes : null,
    };
  }

  toValue(): Omit<JsonOpDaVinciSystemIdPluginDto, 'type'> {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override valid(): boolean {
    return (this.includes && this.includes.length > 0) || (this.excludes && this.excludes.length > 0);
  }

  validationToText(): string {
    if (!this.includes && !this.excludes) {
      return 'Mindestens ein da Vinci System auswählen';
    }

    if (this.includes && this.includes.length === 0) {
      return 'Mindestens ein da Vinci System auswählen';
    } else if (this.excludes && this.excludes.length === 0) {
      return 'Mindestens ein da Vinci System auswählen';
    } else {
      return '';
    }
  }

  toText(): string | null {
    if (!this.includes && !this.excludes) {
      return '';
    }

    if (this.excludes) {
      return this.excludes.length === 1
        ? 'ein da Vinci System'
        : `${this.excludes.length} da Vinci Systeme`;
    }

    return this.includes.length === 1
      ? 'ein da Vinci System'
      : `${this.includes.length} da Vinci Systeme`;
  }
}

class OpDaVinciSystemIdCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(OP_DA_VINCI_SYSTEM_ID_CONDITION_ID, includes, excludes);
  }
}

interface JsonOpDaVinciSystemIdPluginDto {
  type: string;
  includes: string[];
  excludes: string[];
}
